import { getMatchDurationFromConstraints } from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import type { Class, Court } from '../../types/tournament';
import {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../types/schedule';
import {
  getMatchScheduleInterval,
  MatchScheduleInterval,
} from './scheduleConflictDetector';
import {
  buildDePhaseIndexForClass,
  hasSchedulingOrderingViolation,
} from './scheduleOrderingViolation';
import { koIntervalsForPhaseIndex } from './scheduleDePhase';
import {
  areParallelMedalFinals,
  isMedalFinalMatch,
  shouldSkipSwapReverseLegOrdering,
} from './scheduleMedalFinals';
import {
  hasChildBeforeParentConflict,
  hasParentAfterChildConflict,
} from './scheduleParentClass';
import { getPlacementConflictIndex } from './placementConflictIndexCache';
import {
  flattenScheduledMatches,
  MS_PER_MINUTE,
  wouldOverlap,
  wouldOverlapOnCourt,
} from './scheduleOverlap';
import { isBoxLeagueClass, isKnockoutStageMatch } from './scheduleConflictDetector';
import {
  wouldViolateBoxRoundPhaseOnPlace,
  buildGroupIdToBoxIndexMap,
  boxLeagueIntervalsViolateRoundOrder,
  boxRoundOrderingViolation,
  normalizeBoxLeagueRound,
} from './scheduleBoxLeagueRounds';

export type PlacementConflictCheckParams = {
  pending: IMatchScheduleItem;
  courtId: number;
  startMs: number;
  schedule: ITournamentSchedule | null;
  constraints: IScheduleConstraints | null;
  timeZone: string;
  classIdToObject: Record<string, Class>;
  excludeMatchIds?: number[];
  conflictIndex?: PlacementConflictIndex | null;
  lookupMatches?: IMatchScheduleItem[];
};

export type KoOrderingCache = {
  phaseIntervals: MatchScheduleInterval[];
  dePhaseIndex: Map<number, number> | null;
};

export type PlacementConflictIndex = {
  flatMatches: IMatchScheduleItem[];
  intervals: MatchScheduleInterval[];
  intervalsByDayPlayer: Map<number, Map<number, MatchScheduleInterval[]>>;
  intervalsByDayTeam: Map<number, Map<number, MatchScheduleInterval[]>>;
  intervalsByClassId: Map<number, MatchScheduleInterval[]>;
  unscheduledKoByClassId: Map<number, IMatchScheduleItem[]>;
  groupIdToBoxIndex: Map<number, number>;
  matchesByCourtId: Map<number, IMatchScheduleItem[]>;
  courtById: Map<number, Court>;
  koOrderingByClassId: Map<number, KoOrderingCache>;
};

export type PlacementSlotCheckContext = {
  conflictIndex: PlacementConflictIndex;
  excludeIds: Set<number>;
  pending: IMatchScheduleItem;
  courtId: number;
  schedule: ITournamentSchedule;
  constraints: IScheduleConstraints;
  timeZone: string;
  classIdToObject: Record<string, Class>;
  classObject: Class | undefined;
  durationMins: number;
  court: IMatchScheduleItem['court'];
  parentCheckIntervals: MatchScheduleInterval[];
  sameClassIntervals: MatchScheduleInterval[];
};

function toExcludeSet(excludeMatchIds: number[]): Set<number> {
  return new Set(excludeMatchIds);
}

function findCourt(
  schedule: ITournamentSchedule,
  courtId: number
): IMatchScheduleItem['court'] {
  for (const day of schedule.days) {
    for (const courtSchedule of day.courts) {
      if (courtSchedule.court.id === courtId) return courtSchedule.court;
    }
  }
  return { id: courtId, name: `Court ${courtId}` } as IMatchScheduleItem['court'];
}

function buildHypotheticalMatch(
  pending: IMatchScheduleItem,
  courtId: number,
  startMs: number,
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  classIdToObject: Record<string, Class>
): IMatchScheduleItem {
  const classObject =
    classIdToObject[String(pending.classId)] ?? classIdToObject[pending.classId];
  const durationMins =
    pending.duration ||
    getMatchDurationFromConstraints(pending, constraints, classObject) ||
    30;
  const scheduleDate = new Date(startMs).toISOString();
  const scheduleEndDate = new Date(startMs + durationMins * MS_PER_MINUTE).toISOString();
  return {
    ...pending,
    court: findCourt(schedule, courtId),
    scheduleDate,
    scheduleEndDate,
    duration: durationMins,
  };
}

function collectPlayerIds(match: IMatchScheduleItem): number[] {
  const ids: number[] = [];
  for (const p of [
    match.homePlayer1,
    match.homePlayer2,
    match.awayPlayer1,
    match.awayPlayer2,
  ]) {
    if (p?.id != null) ids.push(Number(p.id));
  }
  return ids;
}

function collectTeamIds(match: IMatchScheduleItem): number[] {
  const ids: number[] = [];
  if (match.homeTeam?.id != null) ids.push(Number(match.homeTeam.id));
  if (match.awayTeam?.id != null) ids.push(Number(match.awayTeam.id));
  return ids;
}

function intervalsOverlap(a: MatchScheduleInterval, b: MatchScheduleInterval): boolean {
  if (a.dayKey !== b.dayKey) return false;
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

function sharesEntity(
  a: MatchScheduleInterval,
  b: MatchScheduleInterval
): 'player' | 'team' | null {
  const playersA = new Set(collectPlayerIds(a.match));
  for (const id of collectPlayerIds(b.match)) {
    if (playersA.has(id)) return 'player';
  }
  const teamsA = new Set(collectTeamIds(a.match));
  for (const id of collectTeamIds(b.match)) {
    if (teamsA.has(id)) return 'team';
  }
  return null;
}

function hasOrderingViolation(
  pending: MatchScheduleInterval,
  other: MatchScheduleInterval,
  classIdToObject: Record<string, Class>,
  dePhaseIndex: Map<number, number> | null,
  classIntervals: MatchScheduleInterval[]
): boolean {
  return hasSchedulingOrderingViolation(
    pending,
    other,
    classIdToObject,
    dePhaseIndex,
    classIntervals
  );
}

function addIntervalToEntityMap(
  map: Map<number, Map<number, MatchScheduleInterval[]>>,
  dayKey: number,
  entityId: number,
  interval: MatchScheduleInterval
) {
  let dayMap = map.get(dayKey);
  if (!dayMap) {
    dayMap = new Map();
    map.set(dayKey, dayMap);
  }
  const list = dayMap.get(entityId) ?? [];
  list.push(interval);
  dayMap.set(entityId, list);
}

function classForMatch(
  match: IMatchScheduleItem,
  classIdToObject: Record<string, Class>
): Class | undefined {
  return classIdToObject[String(match.classId)] ?? classIdToObject[match.classId];
}

export function buildPlacementConflictIndex(
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  timeZone: string,
  _excludeMatchIds: number[] = [],
  dayKeys?: number[] | null,
  unscheduledMatches: IMatchScheduleItem[] = [],
  classIdToObject: Record<string, Class> = {}
): PlacementConflictIndex {
  const dayKeySet =
    dayKeys != null && dayKeys.length > 0 ? new Set(dayKeys) : null;
  const allScheduled = flattenScheduledMatches(schedule);
  const flatMatches =
    dayKeySet == null
      ? allScheduled
      : allScheduled.filter((m) => {
          const interval = getMatchScheduleInterval(
            m,
            constraints,
            timeZone,
            classForMatch(m, classIdToObject)
          );
          return interval != null && dayKeySet.has(interval.dayKey);
        });
  const groupIdToBoxIndex = buildGroupIdToBoxIndexMap([
    ...allScheduled,
    ...unscheduledMatches,
  ]);
  const intervals: MatchScheduleInterval[] = [];
  const intervalsByDayPlayer = new Map<number, Map<number, MatchScheduleInterval[]>>();
  const intervalsByDayTeam = new Map<number, Map<number, MatchScheduleInterval[]>>();
  const intervalsByClassId = new Map<number, MatchScheduleInterval[]>();
  const unscheduledKoByClassId = new Map<number, IMatchScheduleItem[]>();

  for (const match of unscheduledMatches) {
    if (!isKnockoutStageMatch(match)) continue;
    const list = unscheduledKoByClassId.get(match.classId) ?? [];
    list.push(match);
    unscheduledKoByClassId.set(match.classId, list);
  }

  for (const match of allScheduled) {
    const interval = getMatchScheduleInterval(
      match,
      constraints,
      timeZone,
      classForMatch(match, classIdToObject)
    );
    if (!interval) continue;
    const classList = intervalsByClassId.get(match.classId) ?? [];
    classList.push(interval);
    intervalsByClassId.set(match.classId, classList);
  }

  const matchesByCourtId = new Map<number, IMatchScheduleItem[]>();
  const courtById = new Map<number, Court>();
  for (const day of schedule.days) {
    for (const courtSchedule of day.courts) {
      courtById.set(courtSchedule.court.id, courtSchedule.court);
    }
  }

  for (const match of flatMatches) {
    const interval = getMatchScheduleInterval(
      match,
      constraints,
      timeZone,
      classForMatch(match, classIdToObject)
    );
    if (!interval) continue;
    intervals.push(interval);

    if (match.court) {
      const courtList = matchesByCourtId.get(match.court.id) ?? [];
      courtList.push(match);
      matchesByCourtId.set(match.court.id, courtList);
    }

    for (const playerId of collectPlayerIds(match)) {
      addIntervalToEntityMap(intervalsByDayPlayer, interval.dayKey, playerId, interval);
    }
    for (const teamId of collectTeamIds(match)) {
      addIntervalToEntityMap(intervalsByDayTeam, interval.dayKey, teamId, interval);
    }
  }

  const koOrderingByClassId = new Map<number, KoOrderingCache>();
  for (const [classId, classIntervals] of intervalsByClassId) {
    const classObject =
      classIdToObject[String(classId)] ?? classIdToObject[classId];
    if (isBoxLeagueClass(classObject)) continue;
    const unscheduledKo = unscheduledKoByClassId.get(classId) ?? [];
    koOrderingByClassId.set(classId, {
      phaseIntervals: koIntervalsForPhaseIndex(classIntervals, unscheduledKo),
      dePhaseIndex: buildDePhaseIndexForClass(classIntervals, classObject, unscheduledKo),
    });
  }

  return {
    flatMatches,
    intervals,
    intervalsByDayPlayer,
    intervalsByDayTeam,
    intervalsByClassId,
    unscheduledKoByClassId,
    groupIdToBoxIndex,
    matchesByCourtId,
    courtById,
    koOrderingByClassId,
  };
}

function hasEntityOverlap(
  pendingInterval: MatchScheduleInterval,
  index: PlacementConflictIndex,
  excludeIds: Set<number>
): boolean {
  const dayKey = pendingInterval.dayKey;
  const dayPlayers = index.intervalsByDayPlayer.get(dayKey);
  if (dayPlayers) {
    for (const playerId of collectPlayerIds(pendingInterval.match)) {
      const others = dayPlayers.get(playerId);
      if (!others) continue;
      for (const other of others) {
        if (excludeIds.has(other.match.id)) continue;
        if (intervalsOverlap(pendingInterval, other) && sharesEntity(pendingInterval, other)) {
          return true;
        }
      }
    }
  }

  const dayTeams = index.intervalsByDayTeam.get(dayKey);
  if (dayTeams) {
    for (const teamId of collectTeamIds(pendingInterval.match)) {
      const others = dayTeams.get(teamId);
      if (!others) continue;
      for (const other of others) {
        if (excludeIds.has(other.match.id)) continue;
        if (intervalsOverlap(pendingInterval, other) && sharesEntity(pendingInterval, other)) {
          return true;
        }
      }
    }
  }

  return false;
}

function sameClassIntervals(
  index: PlacementConflictIndex,
  classId: number,
  excludeIds: Set<number>
): MatchScheduleInterval[] {
  const list = index.intervalsByClassId.get(classId) ?? [];
  if (excludeIds.size === 0) return list;
  return list.filter((interval) => !excludeIds.has(interval.match.id));
}

function hasOrderingConflict(
  pendingInterval: MatchScheduleInterval,
  index: PlacementConflictIndex,
  classIdToObject: Record<string, Class>,
  excludeIds: Set<number>,
  sameClass?: MatchScheduleInterval[]
): boolean {
  const classObject =
    classIdToObject[String(pendingInterval.match.classId)] ??
    classIdToObject[pendingInterval.match.classId];

  const classIntervals =
    sameClass ?? sameClassIntervals(index, pendingInterval.match.classId, excludeIds);

  if (isBoxLeagueClass(classObject)) {
    return wouldViolateBoxRoundPhaseOnPlace(
      pendingInterval,
      classIntervals,
      classObject,
      index.groupIdToBoxIndex
    );
  }

  const koOrdering = index.koOrderingByClassId.get(pendingInterval.match.classId);
  const phaseIntervals = koOrdering?.phaseIntervals ?? classIntervals;
  const dePhaseIndex = koOrdering?.dePhaseIndex ?? null;
  for (const other of classIntervals) {
    if (excludeIds.has(other.match.id)) continue;
    if (
      pendingInterval.match.tournamentGroup?.id &&
      other.match.tournamentGroup?.id
    ) {
      continue;
    }
    if (
      hasOrderingViolation(
        pendingInterval,
        other,
        classIdToObject,
        dePhaseIndex,
        phaseIntervals
      )
    ) {
      return true;
    }
  }
  return false;
}

function hasParentClassConflict(
  pendingInterval: MatchScheduleInterval,
  parentCheckIntervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): boolean {
  if (
    hasChildBeforeParentConflict(
      pendingInterval,
      parentCheckIntervals,
      classIdToObject
    )
  ) {
    return true;
  }
  return hasParentAfterChildConflict(
    pendingInterval,
    parentCheckIntervals,
    classIdToObject
  );
}

function pendingIntervalAt(
  ctx: PlacementSlotCheckContext,
  startMs: number
): MatchScheduleInterval | null {
  const scheduleDate = new Date(startMs).toISOString();
  const scheduleEndDate = new Date(startMs + ctx.durationMins * MS_PER_MINUTE).toISOString();
  const hypothetical: IMatchScheduleItem = {
    ...ctx.pending,
    court: ctx.court,
    scheduleDate,
    scheduleEndDate,
    duration: ctx.durationMins,
  };
  return getMatchScheduleInterval(
    hypothetical,
    ctx.constraints,
    ctx.timeZone,
    ctx.classObject
  );
}

export function createPlacementSlotCheckContext(
  params: Omit<PlacementConflictCheckParams, 'courtId' | 'startMs'> & {
    courtId: number;
    conflictIndex: PlacementConflictIndex;
  }
): PlacementSlotCheckContext {
  const excludeIds = toExcludeSet([params.pending.id, ...(params.excludeMatchIds ?? [])]);
  const classObject =
    params.classIdToObject[String(params.pending.classId)] ??
    params.classIdToObject[params.pending.classId];
  const durationMins =
    params.pending.duration ||
    getMatchDurationFromConstraints(params.pending, params.constraints!, classObject) ||
    30;
  const court =
    params.conflictIndex.courtById.get(params.courtId) ??
    findCourt(params.schedule!, params.courtId);
  const parentCheckIntervals =
    excludeIds.size === 0
      ? params.conflictIndex.intervals
      : params.conflictIndex.intervals.filter(
          (interval) => !excludeIds.has(interval.match.id)
        );
  const sameClass = sameClassIntervals(
    params.conflictIndex,
    params.pending.classId,
    excludeIds
  );
  return {
    conflictIndex: params.conflictIndex,
    excludeIds,
    pending: params.pending,
    courtId: params.courtId,
    schedule: params.schedule!,
    constraints: params.constraints!,
    timeZone: params.timeZone,
    classIdToObject: params.classIdToObject,
    classObject,
    durationMins,
    court,
    parentCheckIntervals,
    sameClassIntervals: sameClass,
  };
}

function canPlaceWithSlotContext(ctx: PlacementSlotCheckContext, startMs: number): boolean {
  const pendingInterval = pendingIntervalAt(ctx, startMs);
  if (!pendingInterval) return false;

  const courtMatches =
    ctx.conflictIndex.matchesByCourtId.get(ctx.courtId) ?? [];
  if (
    wouldOverlapOnCourt(
      startMs,
      ctx.durationMins,
      ctx.courtId,
      ctx.excludeIds,
      courtMatches,
      ctx.constraints
    )
  ) {
    return false;
  }

  if (hasEntityOverlap(pendingInterval, ctx.conflictIndex, ctx.excludeIds)) return false;
  if (
    hasOrderingConflict(
      pendingInterval,
      ctx.conflictIndex,
      ctx.classIdToObject,
      ctx.excludeIds,
      ctx.sameClassIntervals
    )
  ) {
    return false;
  }
  if (
    hasParentClassConflict(
      pendingInterval,
      ctx.parentCheckIntervals,
      ctx.classIdToObject
    )
  ) {
    return false;
  }
  return true;
}

export function canPlaceMatchWithoutConflict({
  pending,
  courtId,
  startMs,
  schedule,
  constraints,
  timeZone,
  classIdToObject,
  excludeMatchIds = [],
  conflictIndex,
  lookupMatches = [],
  slotContext,
}: PlacementConflictCheckParams & { slotContext?: PlacementSlotCheckContext }): boolean {
  if (!schedule || !constraints) {
    console.warn('[schedule] placement conflict check skipped: missing schedule or constraints');
    return false;
  }

  const index =
    conflictIndex ??
    getPlacementConflictIndex(
      schedule,
      constraints,
      timeZone,
      null,
      lookupMatches,
      classIdToObject
    );

  const ctx =
    slotContext ??
    createPlacementSlotCheckContext({
      pending,
      schedule,
      constraints,
      timeZone,
      classIdToObject,
      excludeMatchIds,
      courtId,
      conflictIndex: index,
    });

  return canPlaceWithSlotContext(ctx, startMs);
}

export function canUnscheduledReplaceScheduledSlot(
  pending: IMatchScheduleItem,
  target: IMatchScheduleItem,
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  timeZone: string,
  classIdToObject: Record<string, Class>,
  conflictIndex?: PlacementConflictIndex | null
): boolean {
  if (!target.scheduleDate || !target.court) return false;
  const targetStartMs = new Date(target.scheduleDate).getTime();
  if (!Number.isFinite(targetStartMs)) return false;
  return canPlaceMatchWithoutConflict({
    pending,
    courtId: target.court.id,
    startMs: targetStartMs,
    schedule,
    constraints,
    timeZone,
    classIdToObject,
    excludeMatchIds: [pending.id, target.id],
    conflictIndex,
  });
}

function hasBoxLeagueSwapOrderingViolation(
  source: IMatchScheduleItem,
  target: IMatchScheduleItem,
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  timeZone: string,
  classIdToObject: Record<string, Class>
): boolean {
  if (source.classId !== target.classId) return false;
  const classObject =
    classIdToObject[String(source.classId)] ?? classIdToObject[source.classId];
  if (!classObject || !isBoxLeagueClass(classObject)) return false;
  if (!source.scheduleDate || !source.court || !target.scheduleDate || !target.court) {
    return false;
  }

  const targetStartMs = new Date(target.scheduleDate).getTime();
  const sourceStartMs = new Date(source.scheduleDate).getTime();
  if (!Number.isFinite(targetStartMs) || !Number.isFinite(sourceStartMs)) return false;

  const sourceAtTarget = buildHypotheticalMatch(
    source,
    target.court.id,
    targetStartMs,
    schedule,
    constraints,
    classIdToObject
  );
  const targetAtSource = buildHypotheticalMatch(
    target,
    source.court.id,
    sourceStartMs,
    schedule,
    constraints,
    classIdToObject
  );

  const intervals: MatchScheduleInterval[] = [];
  const scheduled = flattenScheduledMatches(schedule);
  const groupIdToBoxIndex = buildGroupIdToBoxIndexMap([...scheduled, source, target]);
  for (const match of scheduled) {
    if (match.classId !== source.classId) continue;
    let item = match;
    if (match.id === source.id) item = sourceAtTarget;
    else if (match.id === target.id) item = targetAtSource;
    const interval = getMatchScheduleInterval(item, constraints, timeZone, classObject);
    if (interval) intervals.push(interval);
  }

  const sourceAtTargetInterval = getMatchScheduleInterval(
    sourceAtTarget,
    constraints,
    timeZone,
    classObject
  );
  const targetAtSourceInterval = getMatchScheduleInterval(
    targetAtSource,
    constraints,
    timeZone,
    classObject
  );
  const sourceRound = normalizeBoxLeagueRound(source);
  const targetRound = normalizeBoxLeagueRound(target);
  return (
    boxLeagueIntervalsViolateRoundOrder(intervals, classIdToObject, groupIdToBoxIndex) ||
    (sourceAtTargetInterval != null &&
      targetAtSourceInterval != null &&
      sourceRound != null &&
      targetRound != null &&
      sourceRound !== targetRound &&
      boxRoundOrderingViolation(
        sourceAtTargetInterval,
        targetAtSourceInterval,
        classObject,
        groupIdToBoxIndex
      ))
  );
}

export function canSwapMatchesWithoutConflict(
  source: IMatchScheduleItem,
  target: IMatchScheduleItem,
  schedule: ITournamentSchedule | null,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<string, Class>,
  conflictIndex?: PlacementConflictIndex | null
): boolean {
  if (!schedule || !constraints) return true;
  if (source.id === target.id) return true;
  if (!target.scheduleDate || !target.court) return false;

  if (!source.scheduleDate || !source.court) {
    return canUnscheduledReplaceScheduledSlot(
      source,
      target,
      schedule,
      constraints,
      timeZone,
      classIdToObject,
      conflictIndex
    );
  }

  const sourceClass =
    classIdToObject[String(source.classId)] ?? classIdToObject[source.classId];
  const targetClass =
    classIdToObject[String(target.classId)] ?? classIdToObject[target.classId];

  const sourceInterval = getMatchScheduleInterval(
    source,
    constraints,
    timeZone,
    sourceClass
  );
  const targetInterval = getMatchScheduleInterval(
    target,
    constraints,
    timeZone,
    targetClass
  );
  if (
    sourceInterval &&
    targetInterval &&
    intervalsOverlap(sourceInterval, targetInterval) &&
    sharesEntity(sourceInterval, targetInterval)
  ) {
    return false;
  }

  const parallelMedalSiblingIds = isMedalFinalMatch(target)
    ? flattenScheduledMatches(schedule)
        .filter((m) => areParallelMedalFinals(m, target))
        .map((m) => m.id)
    : [];
  const exclude = [source.id, target.id, ...parallelMedalSiblingIds];
  const index =
    conflictIndex ??
    getPlacementConflictIndex(schedule, constraints, timeZone, null, [], classIdToObject);

  const targetStartMs = new Date(target.scheduleDate).getTime();
  const sourceStartMs = new Date(source.scheduleDate).getTime();

  if (
    !canPlaceMatchWithoutConflict({
      pending: source,
      courtId: target.court.id,
      startMs: targetStartMs,
      schedule,
      constraints,
      timeZone,
      classIdToObject,
      excludeMatchIds: exclude,
      conflictIndex: index,
    })
  ) {
    return false;
  }

  if (shouldSkipSwapReverseLegOrdering(source, target)) {
    return !hasBoxLeagueSwapOrderingViolation(
      source,
      target,
      schedule,
      constraints,
      timeZone,
      classIdToObject
    );
  }

  if (
    !canPlaceMatchWithoutConflict({
      pending: target,
      courtId: source.court.id,
      startMs: sourceStartMs,
      schedule,
      constraints,
      timeZone,
      classIdToObject,
      excludeMatchIds: exclude,
      conflictIndex: index,
    })
  ) {
    return false;
  }

  return !hasBoxLeagueSwapOrderingViolation(
    source,
    target,
    schedule,
    constraints,
    timeZone,
    classIdToObject
  );
}

export function filterValidPlacementStartsForConflicts(
  validStartTimesMs: number[],
  courtId: number,
  params: Omit<PlacementConflictCheckParams, 'courtId' | 'startMs'>,
  conflictIndex?: PlacementConflictIndex | null
): number[] {
  if (!params.schedule || !params.constraints) return validStartTimesMs;

  const index =
    conflictIndex ??
    getPlacementConflictIndex(
      params.schedule,
      params.constraints,
      params.timeZone,
      null,
      [],
      params.classIdToObject
    );

  const slotContext = createPlacementSlotCheckContext({
    ...params,
    courtId,
    conflictIndex: index,
  });

  const result: number[] = [];
  for (const startMs of validStartTimesMs) {
    if (canPlaceWithSlotContext(slotContext, startMs)) {
      result.push(startMs);
    }
  }
  return result;
}
